/**
 * Runtime evaluator for Part 1 structured conditionals.
 *
 * Authoring/normalization lives in `@/helpers/cwbConditional`; this module only
 * decides show/hide. Missing-parent rule: a clause whose parent is not in the
 * composed question set is treated as satisfied (child still shows). A parent
 * that is in the set but unanswered is NOT missing — operators decide.
 */
import { normalizeClause } from "@/helpers/cwbConditional";
import { unwrapAnswerValue } from "./answerResolution";

function asList(value) {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
}

function coerceNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function valuesEqual(left, right) {
  if (left === right) return true;
  if (left == null || right == null) return left == null && right == null;
  if (typeof left === "boolean" || typeof right === "boolean") {
    return String(left).toLowerCase() === String(right).toLowerCase();
  }
  if (typeof left === "number" || typeof right === "number") {
    const a = coerceNumber(left);
    const b = coerceNumber(right);
    return a !== null && b !== null && a === b;
  }
  return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
}

function listIncludes(haystack, needles) {
  const pool = new Set(asList(haystack).map((v) => String(v).trim().toLowerCase()));
  return asList(needles).some((needle) => pool.has(String(needle).trim().toLowerCase()));
}

/** Nested AND/OR group = object that carries its own `clauses` array. */
function isNestedGroup(clause) {
  return Boolean(clause && Array.isArray(clause.clauses));
}

/** Normalize a leaf clause; accept legacy parent field names from older payloads. */
function leafClause(clause = {}) {
  return normalizeClause({
    parentCanonicalId:
      clause.parentCanonicalId ??
      clause.parent_canonical_id ??
      clause.parent_canonical_key ??
      clause.parent_key ??
      null,
    operator: clause.operator,
    value: clause.value,
    values: clause.values,
    lower: clause.lower,
    upper: clause.upper,
    min: clause.min,
    max: clause.max,
  });
}

function matchRelational(operator, parentValue, expected) {
  const left = coerceNumber(parentValue);
  const right = coerceNumber(expected);
  if (left === null || right === null) return false;

  const comparisons = {
    ">": left > right,
    ">=": left >= right,
    "<": left < right,
    "<=": left <= right,
  };
  return comparisons[operator] ?? false;
}

function matchInRange(parentValue, normalized) {
  const n = coerceNumber(parentValue);
  if (n === null) return false;

  const lower = coerceNumber(normalized.lower);
  const upper = coerceNumber(normalized.upper);
  if (lower !== null && n < lower) return false;
  if (upper !== null && n > upper) return false;
  return true;
}

function matchOperator(normalized, parentValue) {
  const operator = normalized.operator || "==";

  switch (operator) {
    case "==":
      return valuesEqual(parentValue, normalized.value);
    case "!=":
      return !valuesEqual(parentValue, normalized.value);
    case ">":
    case ">=":
    case "<":
    case "<=":
      return matchRelational(operator, parentValue, normalized.value);
    case "includes":
      return listIncludes(parentValue, normalized.values);
    case "in-range":
      return matchInRange(parentValue, normalized);
    default:
      return false;
  }
}

/**
 * @param {object} clause  raw or normalized leaf clause
 * @param {*} parentValue  unwrapped parent answer value
 */
export function evaluateClause(clause, parentValue) {
  return matchOperator(leafClause(clause), parentValue);
}

/**
 * Parents absent from the composed set → missingParent.
 * Parents present but unanswered → missingParent false, value undefined.
 */
export function readParentAnswer(answersById, parentRef, parentIdsInComposition) {
  const ref = parentRef == null ? "" : String(parentRef).trim();
  if (!ref) return { missingParent: true, value: undefined };

  if (parentIdsInComposition && !parentIdsInComposition.has(ref)) {
    return { missingParent: true, value: undefined };
  }

  const map = answersById || {};
  if (!Object.hasOwn(map, ref)) {
    return { missingParent: false, value: undefined };
  }
  return { missingParent: false, value: unwrapAnswerValue(map[ref]) };
}

/** @returns {boolean} true = show the child question */
export function evaluateConditional(conditional, answersById, parentIdsInComposition = null) {
  if (!conditional) return true;

  const clauses = Array.isArray(conditional.clauses) ? conditional.clauses : [];
  if (!clauses.length) return true;

  const op = (conditional.op || conditional.logic) === "OR" ? "OR" : "AND";
  const results = clauses.map((clause) => {
    if (isNestedGroup(clause)) {
      return evaluateConditional(clause, answersById, parentIdsInComposition);
    }
    const normalized = leafClause(clause || {});
    const { missingParent, value } = readParentAnswer(
      answersById,
      normalized.parentCanonicalId,
      parentIdsInComposition
    );
    if (missingParent) return true;
    return matchOperator(normalized, value);
  });

  return op === "OR" ? results.some(Boolean) : results.every(Boolean);
}

/** Placement `visible: false` or failed conditional → hidden. */
export function isQuestionVisible(question, answersById, parentIdsInComposition = null) {
  if (!question) return false;
  if (question.visible === false) return false;
  return evaluateConditional(question.conditional, answersById, parentIdsInComposition);
}
